import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../services/api'

export default function InvoiceRequestModal({ open, setOpen, planSlug, defaultEmail, onSubmitted }) {
  const { t } = useTranslation()
  const [organisationName, setOrganisationName] = useState('')
  const [orgNumber, setOrgNumber] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [comments, setComments] = useState('')
  const [contactEmail, setContactEmail] = useState(defaultEmail || '')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  // Pre-fill the contact email with the logged-in user's email once known
  useEffect(() => {
    if (defaultEmail && !contactEmail) setContactEmail(defaultEmail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail])

  // After a successful submission, send the user back to the app
  const redirectTimer = useRef(null)
  useEffect(() => {
    if (!submitted) return undefined
    redirectTimer.current = setTimeout(() => {
      window.location.href = t('loginLink')
    }, 3000)
    return () => clearTimeout(redirectTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await api.post('/invoicerequests', {
        organisation_name: organisationName,
        org_number: orgNumber,
        reference_number: referenceNumber,
        comments,
        contact_email: contactEmail,
        plan_slug: planSlug,
      })
      setSubmitted(true)
      if (onSubmitted) onSubmitted()
    } catch (err) {
      console.error('Failed to submit invoice request', err)
      setError(t('invoiceError'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setOpen(false)
    // Reset the success state after the closing transition so a reopened
    // dialog shows the form again
    setTimeout(() => {
      setSubmitted(false)
      setError('')
    }, 300)
  }

  const inputClass =
    'block w-full rounded-md border-0 bg-white px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-smg_orange sm:text-sm/6'

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-md data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            {submitted ? (
              <div className="text-center">
                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" aria-hidden="true" />
                <h3 className="mt-4 text-xl font-semibold text-gray-900">{t('invoiceSuccessTitle')}</h3>
                <p className="mt-2 text-sm text-gray-600">{t('invoiceSuccessText')}</p>
                <a
                  href={t('loginLink')}
                  className="mt-6 block w-full rounded-md bg-smg_orange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-smg_orange_light"
                >
                  {t('goToApp')}
                </a>
                <p className="mt-3 text-xs text-gray-400">{t('invoiceRedirecting')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 className="text-xl font-semibold text-gray-900">{t('invoiceModalTitle')}</h3>
                <p className="mt-2 text-sm text-gray-600">{t('invoiceModalIntro')}</p>

                <div className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="invoice-org-name" className="block text-sm/6 font-medium text-gray-900">
                      {t('invoiceOrgName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="invoice-org-name"
                      type="text"
                      value={organisationName}
                      onChange={(e) => setOrganisationName(e.target.value)}
                      className={inputClass}
                      maxLength={255}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="invoice-org-number" className="block text-sm/6 font-medium text-gray-900">
                      {t('invoiceOrgNumber')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="invoice-org-number"
                      type="text"
                      value={orgNumber}
                      onChange={(e) => setOrgNumber(e.target.value)}
                      className={inputClass}
                      maxLength={64}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="invoice-reference" className="block text-sm/6 font-medium text-gray-900">
                      {t('invoiceReference')}{' '}
                      <span className="font-normal text-gray-500">{t('invoiceOptionalField')}</span>
                    </label>
                    <input
                      id="invoice-reference"
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      className={inputClass}
                      maxLength={255}
                    />
                  </div>
                  <div>
                    <label htmlFor="invoice-contact-email" className="block text-sm/6 font-medium text-gray-900">
                      {t('invoiceContactEmail')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="invoice-contact-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className={inputClass}
                      maxLength={255}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="invoice-comments" className="block text-sm/6 font-medium text-gray-900">
                      {t('invoiceComments')}{' '}
                      <span className="font-normal text-gray-500">{t('invoiceOptionalField')}</span>
                    </label>
                    <textarea
                      id="invoice-comments"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      className={`${inputClass} resize-none`}
                      rows={3}
                      maxLength={2000}
                    />
                  </div>
                </div>

                {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('invoiceCancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-md bg-smg_orange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-smg_orange_light disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                  >
                    {submitting ? t('processing') : t('invoiceSubmit')}
                  </button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
